import { Checkbox, Skeleton } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import ArrowRightIcon from '@untitled-ui/icons-react/build/esm/ArrowRight';
import ChevronRightIcon from '@untitled-ui/icons-react/build/esm/ChevronRight';
import { format } from 'date-fns';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import Label from 'src/components/Label';
import { Scrollbar } from 'src/components/scrollbar';
import { paths } from 'src/paths';
import { previewScreeningFormAction } from 'src/redux/actions/patientScreening';
import { convertDateToSelectedTimeZone } from 'src/utils/convertDateToSelectedTimeZone';

const { patients, patientDetails, screening, sreeningDetails } = paths;

export const ScreeningsTable = ({
  screeningData = [],
  isForEpisodeOfCare = false,
  openPrintMode = false,
  onSelectAllClickScreening,
  selectedScreeningDoc,
  setSelectedScreeningDoc,
  setIsFilterApply,
}: any) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const patientId = state?.patientId;

  const screeningResponse = useSelector((state: any) => state?.getScreeningReducer);

  const formatText = (text: string) =>
    text
      .replace(/_/g, ' ')
      .replace(
        /\w\S*/g,
        (txt: string) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
      );
  const onRowClick = (data: any) => {
    dispatch(previewScreeningFormAction(data));
    navigate('/patients/patient-details/screening/view-document');
  };

  const renderScreeningData = openPrintMode ? screeningData : screeningData?.slice(0, 3);

  return (
    <Box
      sx={{
        marginTop: '32px',
      }}
    >
      <Card>
        {!isForEpisodeOfCare && (
          <CardHeader
            title={
              <>
                <Typography
                  variant="h6"
                  display={'inline-block'}
                >
                  Screenings
                </Typography>
                <Box
                  sx={{
                    borderRadius: '12px',
                    border: '1px solid rgba(0, 0, 0, 0.26)',
                    display: 'inline-block',
                    paddingTop: '3px',
                    paddingLeft: '6px',
                    paddingRight: '6px',
                    paddingBottom: '3px',
                  }}
                  ml={2}
                >
                  <Typography variant="subtitle2">{`${screeningData?.length} Screenings`}</Typography>
                </Box>
              </>
            }
          />
        )}
        <Divider />
        <Scrollbar>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell sortDirection="desc">
                  {' '}
                  {openPrintMode ? (
                    <Checkbox
                      color="primary"
                      indeterminate={
                        selectedScreeningDoc?.length > 0 &&
                        selectedScreeningDoc?.length < screeningData?.length
                      }
                      checked={
                        screeningData?.length > 0 &&
                        selectedScreeningDoc?.length === screeningData?.length
                      }
                      onChange={onSelectAllClickScreening}
                      inputProps={{
                        'aria-label': 'select all desserts',
                      }}
                    />
                  ) : null}
                  Screening Type
                </TableCell>
                <TableCell>Eval Discipline</TableCell>
                <TableCell>Screening Date</TableCell>
                <TableCell>status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {screeningResponse?.loader ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box
                      textAlign="center"
                      p={3}
                    >
                      <Skeleton animation="wave" />
                      <Skeleton animation="wave" />
                      <Skeleton animation="wave" />
                      <Skeleton animation="wave" />
                      <Skeleton animation="wave" />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : screeningData?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box
                      textAlign="center"
                      p={3}
                    >
                      <Typography
                        color={'text.secondary'}
                        variant="subtitle2"
                      >
                        No Records Found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                renderScreeningData?.slice(0, 3)?.map((screening: any) => {
                  const screeningDateFormat = screening?.screeningDate?.split('T')?.at(0);
                  const screeningDateWithoutTimezone = new Date(
                    new Date(screeningDateFormat).valueOf() +
                      new Date(screeningDateFormat).getTimezoneOffset() * 60 * 1000
                  );
                  return (
                    <TableRow
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: selectedScreeningDoc?.some(
                          (item: any) => item?.uuid === screening?.uuid
                        )
                          ? '#EBEEFE'
                          : null,
                      }}
                      key={screening?.uuid}
                      onClick={() => {
                        if (openPrintMode) {
                          setIsFilterApply(false);
                          const chipIndex = selectedScreeningDoc?.find(
                            (chip: any) => chip?.uuid === screening?.uuid
                          );
                          if (chipIndex) {
                            const updatedChips = selectedScreeningDoc?.filter(
                              (chip: any) => chip?.uuid !== screening?.uuid
                            );
                            setSelectedScreeningDoc(updatedChips);
                            return;
                          } else {
                            setSelectedScreeningDoc([...selectedScreeningDoc, screening]);
                          }
                          return;
                        }
                        onRowClick(screening);
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {openPrintMode ? (
                            <Checkbox
                              checked={selectedScreeningDoc?.some(
                                (item: any) => item?.uuid === screening?.uuid
                              )}
                            />
                          ) : null}
                          <Typography variant="subtitle2">
                            {screening?.screeningType ? formatText(screening?.screeningType) : '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {!!screening?.discipline ? screening?.discipline : '-'}{' '}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {!!screening?.screeningDate
                            ? moment(screeningDateWithoutTimezone)?.format('MMM DD, yyyy')
                            : '-'}{' '}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {/* {screening?.active ? ( */}
                        <Label
                          backgroundColor="#15B79E1F"
                          color="#107569"
                          text="COMPLETED"
                        />
                        {/* ) : (
                          <Label
                            backgroundColor="#F790091F"
                            color="#B54708"
                            text="IN-PROGRESS"
                          />
                        )} */}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton>
                          <SvgIcon>
                            <ArrowRightIcon />
                          </SvgIcon>
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Scrollbar>

        {!isForEpisodeOfCare && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              p: 2,
            }}
          >
            <Button
              color="primary"
              endIcon={
                <SvgIcon>
                  <ChevronRightIcon />
                </SvgIcon>
              }
              size="small"
              // onClick={(event) =>
              // navigate(`${patients}/${patientDetails}${screening}`, {
              //   state: {
              //     patientId,
              //   },
              // })
              // }

              onClick={(event) => {
                if (openPrintMode) {
                  return;
                }
                navigate(`${patients}/${patientDetails}${screening}`, {
                  state: {
                    patientId,
                  },
                });
              }}
            >
              See All Screenings
            </Button>
          </Box>
        )}
      </Card>
    </Box>
  );
};
